import { bufferCount, distinctUntilChanged, filter, map, Observable, scan, Subject, Subscription } from 'rxjs';
import { v4 } from 'uuid';
import { MonitoringConfig } from './monitoring-config';
import { MotionMasterPubSubSocket } from './motion-master-pub-sub-socket';
import { selectMotionMasterMessageByTopic, selectMotionMasterMessageStatusByKey } from './operators';
import { IMotionMasterMessage, MotionMasterMessage } from './types';
import equal from 'fast-deep-equal';
import { MotionMasterPubSubWorkerSocket } from './motion-master-pub-sub-worker-socket';
import { unitConverters } from './units';

export class MotionMasterPubSubClient {
  readonly data$ = new Subject<[string, IMotionMasterMessage[]]>();

  private subscriptions = new Map<string, Subscription>();

  readonly notification$: Observable<IMotionMasterMessage>;

  readonly systemEvent$: Observable<MotionMasterMessage.Status.ISystemEvent>;

  readonly deviceEvent$: Observable<MotionMasterMessage.Status.IDeviceEvent>;

  constructor(public readonly socket: MotionMasterPubSubSocket) {
    // These observables are assigned here rather than via field initializers because they reference
    // `this.socket`. Under native class-field semantics (used by Bun, which ignores
    // `useDefineForClassFields`), field initializers run before the constructor body assigns the
    // parameter property, leaving `this.socket` undefined. Assigning here guarantees correct order.
    this.notification$ = this.socket.data$.pipe(selectMotionMasterMessageByTopic('notification'));
    this.systemEvent$ = this.notification$.pipe(
      selectMotionMasterMessageStatusByKey<MotionMasterMessage.Status.ISystemEvent>('systemEvent')
    );
    this.deviceEvent$ = this.notification$.pipe(
      selectMotionMasterMessageStatusByKey<MotionMasterMessage.Status.IDeviceEvent>('deviceEvent')
    );
  }

  get messageIds(): string[] {
    return Array.from(this.subscriptions.keys());
  }

  subscribe(config: MonitoringConfig): string {
    const messageId = config.messageId ?? v4();
    if (!config.messageId) {
      config = { ...config, messageId };
    }

    if (this.socket instanceof MotionMasterPubSubWorkerSocket) {
      this.socket.worker.postMessage({ subscribe: config });
    } else {
      const { bufferSize = 1, distinct = false, topic, unitFormatters } = config;

      let observable: Observable<IMotionMasterMessage> = this.socket.data$.pipe(
        filter((data) => data[0] === topic),
        map((data) => data[1])
      );

      if (distinct) {
        observable = observable.pipe(
          distinctUntilChanged((prev, curr) => {
            if (
              prev?.status?.monitoringParameterValues?.deviceParameterValues?.parameterValues &&
              curr?.status?.monitoringParameterValues?.deviceParameterValues?.parameterValues
            ) {
              return equal(
                prev.status.monitoringParameterValues.deviceParameterValues.parameterValues,
                curr.status.monitoringParameterValues.deviceParameterValues.parameterValues
              );
            }
            return false;
          })
        );
      }

      if (unitFormatters) {
        observable = observable.pipe(
          map((message) => {
            if (unitFormatters) {
              const values = message.status?.monitoringParameterValues?.deviceParameterValues?.parameterValues;
              if (values && unitFormatters) {
                if (values.length > 0 && values.length !== unitFormatters.length) {
                  console.warn(
                    'The number of unit formatters does not correspond to the number of values. Unit formatting will not be applied.'
                  );
                  return message;
                }
                for (let i = 0; i < values.length; i++) {
                  const formatter = unitFormatters[i];
                  if (formatter) {
                    const converter = unitConverters[formatter.converterId];
                    if (converter) {
                      if (values[i].intValue !== undefined) {
                        // The uintValue will store a float value when formatted, rather than an unsigned integer.
                        // Unlike position values from the drive, which are integers, degrees and revolutions in the UI are represented as float values.
                        values[i].intValue = parseFloat(
                          converter.formatter(formatter.unit, values[i].intValue as number, formatter.data)
                        );
                      } else if (values[i].uintValue !== undefined) {
                        // The uintValue will store a float value when formatted, rather than an unsigned integer.
                        // Unlike position values from the drive, which are integers, degrees and revolutions in the UI are represented as float values.
                        values[i].uintValue = parseFloat(
                          converter.formatter(formatter.unit, values[i].uintValue as number, formatter.data)
                        );
                      } else if (values[i].floatValue !== undefined) {
                        values[i].floatValue = parseFloat(
                          converter.formatter(formatter.unit, values[i].floatValue as number, formatter.data)
                        );
                      }
                    }
                  }
                }
              }
            }
            return message;
          })
        );
      }

      const subscription = observable.pipe(bufferCount(bufferSize)).subscribe((data) => {
        this.data$.next([topic, data]);
      });

      this.subscriptions.set(messageId, subscription);
    }

    return messageId;
  }

  unsubscribe(messageId: string): void {
    if (this.socket instanceof MotionMasterPubSubWorkerSocket) {
      this.socket.worker.postMessage({ unsubcribe: { messageId } });
    } else {
      const subscription = this.subscriptions.get(messageId);
      if (subscription) {
        subscription.unsubscribe();
        this.subscriptions.delete(messageId);
      }
    }
  }

  unsubscribeAll(): void {
    if (this.socket instanceof MotionMasterPubSubWorkerSocket) {
      this.socket.worker.postMessage({ unsubscribeAll: true });
    } else {
      this.subscriptions.forEach((subscription) => subscription.unsubscribe());
      this.subscriptions.clear();
    }
  }

  createSystemStatesObservable(): Observable<MotionMasterMessage.Status.SystemEvent.State[]> {
    return this.systemEvent$.pipe(
      map(({ state }) => state),
      filter(Boolean), // this will exclude the UNSPECIFIED state
      scan((acc: MotionMasterMessage.Status.SystemEvent.State[], state) => {
        acc.push(state);
        return acc;
      }, [])
    );
  }
}
